import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import {
  Star, Clock, CheckCircle, XCircle, X, Building2,
  Send, MessageSquare, ThumbsUp, ThumbsDown, Eye,
  HeartHandshake, Briefcase, User, MapPin, Calendar, AlertTriangle, ExternalLink,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { FeedbackRequest, AlumniEmployerFeedback, FeedbackRelationship } from '../types'
import { EMPLOYMENT_TYPE_LABELS, EXPERIENCE_LEVEL_LABELS, SALARY_LABELS } from '../constants/profileOptions'

// ─── Helpers ──────────────────────────────────────────────────
function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const RELATIONSHIP_LABELS: Record<string, string> = { employee: 'Employee', intern: 'Intern' }

// ─── Star Input ───────────────────────────────────────────────
const StarInput: React.FC<{ value: number; onChange: (v: number) => void; label: string }> = ({ value, onChange, label }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-700">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" onClick={() => onChange(i)} className="p-0.5">
          <Star className={`w-5 h-5 transition ${i <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`} />
        </button>
      ))}
    </div>
  </div>
)

// ─── Stars display ────────────────────────────────────────────
const Stars: React.FC<{ value: number }> = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`w-4 h-4 ${i <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
    ))}
  </div>
)

// ─── Company Info Modal ───────────────────────────────────────
interface CompanyInfoModalProps {
  request: FeedbackRequest
  onClose: () => void
}

const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({ request, onClose }) => createPortal(
  <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b">
        <h2 className="text-base font-bold text-gray-900">About This Company</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition">
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Company logo + name */}
        <div className="flex items-center gap-4">
          {request.company_logo ? (
            <img src={request.company_logo} alt={request.company_name} className="w-16 h-16 rounded-2xl object-cover border border-gray-100 shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center shrink-0">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
          )}
          <div>
            <h3 className="text-lg font-bold text-gray-900">{request.company_name}</h3>
            {request.company_industry && <p className="text-sm text-gray-500 mt-0.5">{request.company_industry}</p>}
          </div>
        </div>

        {/* Info badges */}
        {(request.company_size_text) && (
          <div className="flex flex-wrap gap-2">
            {request.company_size_text && (
              <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                <Building2 className="w-3.5 h-3.5" /> {request.company_size_text}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        {request.company_description && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-1.5">About</h4>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{request.company_description}</p>
          </div>
        )}

        {/* Website */}
        {request.company_website && (
          <a
            href={request.company_website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium break-all"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            {request.company_website}
          </a>
        )}

        {/* Employer contact */}
        {request.contact_person && (
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Employer Representative</h4>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{request.contact_person}</p>
                <p className="text-xs text-gray-400">Hiring Representative</p>
              </div>
            </div>
          </div>
        )}

        {/* Fallback */}
        {!request.company_description && !request.company_website && !request.company_industry && !request.company_size_text && !request.contact_person && (
          <p className="text-sm text-gray-400 text-center py-2">No additional company info available.</p>
        )}
      </div>

      <div className="flex justify-end px-5 pb-5">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition">Close</button>
      </div>
    </div>
  </div>,
  document.body
)

// ─── Submit Feedback Modal ────────────────────────────────────
interface SubmitModalProps {
  request: FeedbackRequest
  onClose: () => void
  onSubmitted: () => void
}

const SubmitFeedbackModal: React.FC<SubmitModalProps> = ({ request, onClose, onSubmitted }) => {
  const [workEnv, setWorkEnv] = useState(0)
  const [mentorship, setMentorship] = useState(0)
  const [careerGrowth, setCareerGrowth] = useState(0)
  const [compensation, setCompensation] = useState(0)
  const [overall, setOverall] = useState(0)
  const [comment, setComment] = useState('')
  const [strengths, setStrengths] = useState('')
  const [improvements, setImprovements] = useState('')
  const [wouldRecommend, setWouldRecommend] = useState(true)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const allRated = workEnv > 0 && mentorship > 0 && careerGrowth > 0 && compensation > 0 && overall > 0

  const handleSubmit = async () => {
    if (!allRated) { toast.error('Please rate all categories'); return }
    setSubmitting(true)
    try {
      const { data, error } = await supabaseClient.rpc('submit_alumni_feedback', {
        p_request_id: request.id,
        p_work_environment: workEnv,
        p_mentorship: mentorship,
        p_career_growth: careerGrowth,
        p_compensation: compensation,
        p_overall_rating: overall,
        p_overall_comment: comment || null,
        p_strengths: strengths || null,
        p_improvements: improvements || null,
        p_would_recommend: wouldRecommend,
        p_is_anonymous: isAnonymous,
      })
      if (error) throw error
      const result = data as { success: boolean; error?: string }
      if (!result.success) throw new Error(result.error || 'Failed to submit')
      toast.success('Feedback submitted!')
      onSubmitted()
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Star className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Submit Feedback</h3>
              <p className="text-sm text-gray-500">{request.company_name} · {RELATIONSHIP_LABELS[request.relationship]}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Ratings */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
            <p className="text-sm font-semibold text-gray-700 mb-1">Rate your experience</p>
            <StarInput label="Work Environment" value={workEnv} onChange={setWorkEnv} />
            <StarInput label="Mentorship" value={mentorship} onChange={setMentorship} />
            <StarInput label="Career Growth" value={careerGrowth} onChange={setCareerGrowth} />
            <StarInput label="Compensation" value={compensation} onChange={setCompensation} />
            <StarInput label="Overall Rating" value={overall} onChange={setOverall} />
          </div>

          {/* Overall comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Overall Comment</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share your overall experience…"
              rows={3}
              maxLength={1000}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Strengths */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Strengths</label>
            <textarea
              value={strengths}
              onChange={e => setStrengths(e.target.value)}
              placeholder="What did the company do well?"
              rows={2}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Improvements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Areas for Improvement</label>
            <textarea
              value={improvements}
              onChange={e => setImprovements(e.target.value)}
              placeholder="What could be improved?"
              rows={2}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Would recommend */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <span className="text-sm text-gray-700 font-medium">Would you recommend this company?</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setWouldRecommend(true)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                  wouldRecommend ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <ThumbsUp className="w-4 h-4" /> Yes
              </button>
              <button
                type="button"
                onClick={() => setWouldRecommend(false)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                  !wouldRecommend ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <ThumbsDown className="w-4 h-4" /> No
              </button>
            </div>
          </div>

          {/* Anonymous toggle */}
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={e => setIsAnonymous(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Submit anonymously</span>
              <p className="text-xs text-gray-400">The employer will see your ratings and comments but not your identity.</p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end p-5 border-t sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !allRated}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── View Request Detail Modal ───────────────────────────────
interface ViewRequestDetailModalProps {
  request: FeedbackRequest
  onClose: () => void
  onAccept: () => void
  onDecline: () => void
  onSubmit: () => void
  responding: boolean
}

const ViewRequestDetailModal: React.FC<ViewRequestDetailModalProps> = ({
  request: r, onClose, onAccept, onDecline, onSubmit, responding,
}) => createPortal(
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {r.company_logo ? (
            <img src={r.company_logo} className="w-11 h-11 rounded-xl object-cover border border-gray-100 flex-shrink-0" />
          ) : (
            <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">{r.company_name}</h3>
            <p className="text-xs text-gray-500">{RELATIONSHIP_LABELS[r.relationship]} · Requested by {r.contact_person ?? 'Employer'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {r.status === 'pending' && (
            <span className="flex items-center gap-1 text-yellow-600 bg-yellow-50 text-xs font-medium px-2.5 py-1 rounded-full">
              <Clock className="w-3 h-3" /> Pending
            </span>
          )}
          {r.status === 'accepted' && (
            <span className="flex items-center gap-1 text-blue-600 bg-blue-50 text-xs font-medium px-2.5 py-1 rounded-full">
              <CheckCircle className="w-3 h-3" /> Accepted
            </span>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Request date + message */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Clock className="w-3.5 h-3.5" /> Requested on {formatDate(r.requested_at)}
        </div>

        {r.message && (
          <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-blue-300">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Message from Employer</p>
            <p className="text-sm text-gray-700 italic">"{r.message}"</p>
          </div>
        )}

        {/* Job details */}
        {r.job_title ? (
          <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-gray-800">Job Position</h4>
              {r.job_deleted ? (
                <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <AlertTriangle className="w-3 h-3" /> Deleted
                </span>
              ) : r.job_status === 'closed' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <XCircle className="w-3 h-3" /> Closed
                </span>
              ) : r.job_status === 'active' ? (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex-shrink-0">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              ) : null}
            </div>

            <div>
              <p className="font-bold text-gray-900 text-base">{r.job_title}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
                {r.job_employment_type && (
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {EMPLOYMENT_TYPE_LABELS[r.job_employment_type as keyof typeof EMPLOYMENT_TYPE_LABELS] ?? r.job_employment_type}
                  </span>
                )}
                {r.job_experience_level && (
                  <span>{EXPERIENCE_LEVEL_LABELS[r.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? r.job_experience_level}</span>
                )}
                {r.job_location && (
                  <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-gray-400" />{r.job_location}</span>
                )}
                {r.job_salary_range && (
                  <span className="text-green-700 font-medium">{SALARY_LABELS[r.job_salary_range as keyof typeof SALARY_LABELS] ?? r.job_salary_range}</span>
                )}
                {r.job_deadline && (
                  <span className={`flex items-center gap-0.5 font-medium ${new Date(r.job_deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                    <Calendar className="w-3 h-3" />
                    {new Date(r.job_deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(r.job_deadline)}
                  </span>
                )}
              </div>
            </div>

            {r.job_description && (
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-4 whitespace-pre-line">{r.job_description}</p>
            )}

            {r.job_requirements && r.job_requirements.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {r.job_requirements.map((req, i) => <li key={i} className="text-sm text-gray-700">{req}</li>)}
                </ul>
              </div>
            )}

            {r.job_skills_required && r.job_skills_required.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                <div className="flex flex-wrap gap-1.5">
                  {r.job_skills_required.map((s, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
            <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            No job details available for this request.
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t flex items-center justify-between gap-3 flex-shrink-0">
        <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition">Close</button>
        {r.status === 'pending' && (
          <div className="flex gap-2">
            <button
              onClick={() => { onClose(); onDecline() }}
              disabled={responding}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" /> Decline
            </button>
            <button
              onClick={() => { onClose(); onAccept() }}
              disabled={responding}
              className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" /> Accept
            </button>
          </div>
        )}
        {r.status === 'accepted' && (
          <button
            onClick={() => { onClose(); onSubmit() }}
            className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition"
          >
            <Star className="w-4 h-4" /> Submit Feedback
          </button>
        )}
      </div>
    </div>
  </div>,
  document.body
)

// ─── View Submitted Modal ─────────────────────────────────────
interface ViewSubmittedModalProps {
  feedback: AlumniEmployerFeedback
  onClose: () => void
}

const ViewSubmittedModal: React.FC<ViewSubmittedModalProps> = ({ feedback, onClose }) => {
  const categories = [
    { label: 'Work Environment', value: feedback.work_environment },
    { label: 'Mentorship',       value: feedback.mentorship },
    { label: 'Career Growth',    value: feedback.career_growth },
    { label: 'Compensation',     value: feedback.compensation },
    { label: 'Overall Rating',   value: feedback.overall_rating },
  ]
  const avg = categories.reduce((s, c) => s + c.value, 0) / categories.length

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Your Feedback</h3>
              <p className="text-sm text-gray-500">{feedback.company_name} · {RELATIONSHIP_LABELS[feedback.relationship]}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Job details */}
          {feedback.job_title ? (
            <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-blue-500 shrink-0" /> Job Position
                </h4>
                {feedback.job_deleted ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full shrink-0">
                    <AlertTriangle className="w-3 h-3" /> Deleted
                  </span>
                ) : feedback.job_status === 'closed' ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full shrink-0">
                    <XCircle className="w-3 h-3" /> Closed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full shrink-0">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
              <div>
                <p className="font-bold text-gray-900">{feedback.job_title}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
                  {feedback.job_employment_type && (
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {EMPLOYMENT_TYPE_LABELS[feedback.job_employment_type as keyof typeof EMPLOYMENT_TYPE_LABELS] ?? feedback.job_employment_type}
                    </span>
                  )}
                  {feedback.job_experience_level && <span>{EXPERIENCE_LEVEL_LABELS[feedback.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? feedback.job_experience_level}</span>}
                  {feedback.job_location && (
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-gray-400" />{feedback.job_location}</span>
                  )}
                  {feedback.job_salary_range && (
                    <span className="text-green-700 font-medium">{SALARY_LABELS[feedback.job_salary_range as keyof typeof SALARY_LABELS] ?? feedback.job_salary_range}</span>
                  )}
                  {feedback.job_deadline && (
                    <span className={`flex items-center gap-0.5 font-medium ${new Date(feedback.job_deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                      <Calendar className="w-3 h-3" />
                      {new Date(feedback.job_deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(feedback.job_deadline)}
                    </span>
                  )}
                </div>
              </div>
              {feedback.job_description && (
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{feedback.job_description}</p>
              )}
              {feedback.job_requirements && feedback.job_requirements.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {feedback.job_requirements.map((r, i) => <li key={i} className="text-sm text-gray-600">{r}</li>)}
                  </ul>
                </div>
              )}
              {feedback.job_skills_required && feedback.job_skills_required.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                  <div className="flex flex-wrap gap-1.5">
                    {feedback.job_skills_required.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400 text-sm bg-gray-50 rounded-xl">
              <Briefcase className="w-7 h-7 mx-auto mb-1.5 text-gray-300" />
              No job details linked to this feedback.
            </div>
          )}

          <div className="text-center p-4 bg-blue-50 rounded-xl">
            <p className="text-3xl font-bold text-blue-900">{avg.toFixed(1)}</p>
            <Stars value={Math.round(avg)} />
            <p className="text-xs text-blue-600 mt-1">Your Average Rating</p>
          </div>

          <div className="space-y-3">
            {categories.map(c => (
              <div key={c.label} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{c.label}</span>
                <div className="flex items-center gap-2">
                  <Stars value={c.value} />
                  <span className="text-sm font-medium text-gray-900 w-5 text-right">{c.value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50">
            {feedback.would_recommend ? (
              <><ThumbsUp className="w-5 h-5 text-green-600" /><span className="text-sm text-green-700 font-medium">You recommended this company</span></>
            ) : (
              <><ThumbsDown className="w-5 h-5 text-red-600" /><span className="text-sm text-red-700 font-medium">You did not recommend</span></>
            )}
          </div>

          {feedback.is_anonymous && (
            <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 px-3 py-2 rounded-lg">
              <User className="w-3.5 h-3.5" /> Submitted anonymously
            </div>
          )}

          {feedback.overall_comment && (
            <div><h4 className="text-sm font-semibold text-gray-700 mb-1">Overall Comment</h4><p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{feedback.overall_comment}</p></div>
          )}
          {feedback.strengths && (
            <div><h4 className="text-sm font-semibold text-gray-700 mb-1">Strengths</h4><p className="text-sm text-gray-600 bg-green-50 rounded-xl p-3">{feedback.strengths}</p></div>
          )}
          {feedback.improvements && (
            <div><h4 className="text-sm font-semibold text-gray-700 mb-1">Areas for Improvement</h4><p className="text-sm text-gray-600 bg-yellow-50 rounded-xl p-3">{feedback.improvements}</p></div>
          )}
        </div>

        <div className="p-5 border-t flex justify-between items-center">
          <span className="text-xs text-gray-400">Submitted {formatDate(feedback.submitted_at)}</span>
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition">Close</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Main Page ────────────────────────────────────────────────
type Tab = 'requests' | 'submitted'

export default function AlumniProvideFeedbackPage() {
  const { user } = useAuthStore()
  const [tab, setTab] = useState<Tab>('requests')

  const [requests, setRequests] = useState<FeedbackRequest[]>([])
  const [submitted, setSubmitted] = useState<AlumniEmployerFeedback[]>([])
  const [loading, setLoading] = useState(true)

  const [respondingId, setRespondingId] = useState<string | null>(null)
  const [submitTarget, setSubmitTarget] = useState<FeedbackRequest | null>(null)
  const [viewSubmitted, setViewSubmitted] = useState<AlumniEmployerFeedback | null>(null)
  const [viewRequest, setViewRequest] = useState<FeedbackRequest | null>(null)
  const [companyModalRequest, setCompanyModalRequest] = useState<FeedbackRequest | null>(null)

  const fetchAll = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const [{ data: reqs }, { data: subs }] = await Promise.all([
        supabaseClient.rpc('get_feedback_requests_received'),
        supabaseClient.rpc('get_my_submitted_feedback'),
      ])
      setRequests((reqs as FeedbackRequest[]) ?? [])
      setSubmitted((subs as AlumniEmployerFeedback[]) ?? [])
    } catch {
      toast.error('Failed to load feedback data')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => { fetchAll() }, [fetchAll])

  const handleRespond = async (requestId: string, response: 'accepted' | 'declined') => {
    setRespondingId(requestId)
    try {
      const { data, error } = await supabaseClient.rpc('respond_to_feedback_request', {
        p_request_id: requestId,
        p_response: response,
      })
      if (error) throw error
      const result = data as { success: boolean; error?: string }
      if (!result.success) throw new Error(result.error || 'Failed')
      toast.success(response === 'accepted' ? 'Request accepted! You can now submit feedback.' : 'Request declined.')
      fetchAll()
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to respond')
    } finally {
      setRespondingId(null)
    }
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length
  const acceptedCount = requests.filter(r => r.status === 'accepted').length

  const TABS: { key: Tab; label: string; count?: number }[] = [
    { key: 'requests',  label: 'Requests',  count: requests.length },
    { key: 'submitted', label: 'Submitted', count: submitted.length },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Hero Banner ─────────────────────────────────────── */}
      <div className="bg-blue-900 text-white rounded-2xl overflow-hidden">
        <div className="px-6 sm:px-8 py-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <HeartHandshake className="w-8 h-8 text-yellow-400" />
              <h1 className="text-2xl sm:text-3xl font-bold">Provide Feedback</h1>
            </div>
            <p className="text-blue-200 max-w-xl">Share your experience working with employers to help future alumni make informed decisions.</p>
            <div className="flex flex-wrap gap-3 mt-4">
              {pendingCount > 0 && (
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  <span>{pendingCount} Pending</span>
                </div>
              )}
              {acceptedCount > 0 && (
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>{acceptedCount} To Submit</span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>{submitted.length} Submitted</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                tab === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
              {(t.count ?? 0) > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ─── Requests Tab ─────────────────────────────── */}
            {tab === 'requests' && (
              requests.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No feedback requests</p>
                  <p className="text-gray-400 text-sm mt-1">When an employer requests your feedback, it will appear here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {requests.map(r => (
                    <div key={r.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition p-5 cursor-pointer" onClick={() => setViewRequest(r)}>
                      {/* Company info */}
                      <div className="flex items-center gap-3 mb-3">
                        {r.company_logo ? (
                          <img
                            src={r.company_logo}
                            className="w-12 h-12 rounded-xl object-cover border border-gray-100 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400 transition"
                            title="View company info"
                            onClick={e => { e.stopPropagation(); setCompanyModalRequest(r) }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400 transition"
                            title="View company info"
                            onClick={e => { e.stopPropagation(); setCompanyModalRequest(r) }}
                          >
                            <Building2 className="w-6 h-6 text-blue-600" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{r.company_name}</p>
                          <p className="text-xs text-gray-500">{RELATIONSHIP_LABELS[r.relationship]} · Requested by {r.contact_person ?? 'Employer'}</p>
                        </div>
                      </div>

                      {/* Job details */}
                      {r.job_title && (
                        <div className="flex items-start gap-2 bg-blue-50 rounded-xl px-3 py-2.5 mb-3">
                          <Briefcase className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-blue-900 truncate">{r.job_title}</p>
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-xs text-blue-700">
                              {r.job_employment_type && <span>{EMPLOYMENT_TYPE_LABELS[r.job_employment_type as keyof typeof EMPLOYMENT_TYPE_LABELS] ?? r.job_employment_type}</span>}
                              {r.job_experience_level && <span>· {EXPERIENCE_LEVEL_LABELS[r.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? r.job_experience_level}</span>}
                              {r.job_location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{r.job_location}</span>}
                              {r.job_salary_range && <span>· {SALARY_LABELS[r.job_salary_range as keyof typeof SALARY_LABELS] ?? r.job_salary_range}</span>}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Status + date */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDate(r.requested_at)}</span>
                        {r.status === 'pending' && (
                          <span className="flex items-center gap-1 text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full font-medium">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        )}
                        {r.status === 'accepted' && (
                          <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                            <CheckCircle className="w-3 h-3" /> Accepted
                          </span>
                        )}
                      </div>

                      {r.message && <p className="text-sm text-gray-600 italic bg-gray-50 rounded-lg p-3 mb-4 line-clamp-3">"{r.message}"</p>}

                      {/* Actions */}
                      {r.status === 'pending' && (
                        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleRespond(r.id, 'accepted')}
                            disabled={respondingId === r.id}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" /> Accept
                          </button>
                          <button
                            onClick={() => handleRespond(r.id, 'declined')}
                            disabled={respondingId === r.id}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
                          >
                            <XCircle className="w-4 h-4" /> Decline
                          </button>
                        </div>
                      )}
                      {r.status === 'accepted' && (
                        <button
                          onClick={e => { e.stopPropagation(); setSubmitTarget(r) }}
                          className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition"
                        >
                          <Star className="w-4 h-4" /> Submit Feedback
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}

            {/* ─── Submitted Tab ────────────────────────────── */}
            {tab === 'submitted' && (
              submitted.length === 0 ? (
                <div className="text-center py-16">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No feedback submitted yet</p>
                  <p className="text-gray-400 text-sm mt-1">Your submitted feedback will appear here for reference.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {submitted.map(f => {
                    const avg = (f.work_environment + f.mentorship + f.career_growth + f.compensation + f.overall_rating) / 5
                    return (
                      <div key={f.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition p-5 cursor-pointer flex flex-col" onClick={() => setViewSubmitted(f)}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {f.company_logo ? (
                              <img src={f.company_logo} className="w-10 h-10 rounded-xl object-cover border border-gray-100" />
                            ) : (
                              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-blue-600" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{f.company_name}</p>
                              <p className="text-xs text-gray-500">{RELATIONSHIP_LABELS[f.relationship]} · {formatDate(f.submitted_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 bg-yellow-50 text-yellow-700 font-bold text-sm px-2.5 py-1 rounded-full">
                            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /> {avg.toFixed(1)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <Stars value={Math.round(avg)} />
                          {f.is_anonymous && <span className="text-xs text-gray-400 ml-auto">Anonymous</span>}
                        </div>
                        {f.job_title && (
                          <div className="flex items-start gap-2 bg-blue-50 rounded-xl px-3 py-2.5 mb-3">
                            <Briefcase className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-blue-900 truncate">{f.job_title}</p>
                              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-xs text-blue-700">
                                {f.job_employment_type && <span>{EMPLOYMENT_TYPE_LABELS[f.job_employment_type as keyof typeof EMPLOYMENT_TYPE_LABELS] ?? f.job_employment_type}</span>}
                                {f.job_experience_level && <span>· {EXPERIENCE_LEVEL_LABELS[f.job_experience_level as keyof typeof EXPERIENCE_LEVEL_LABELS] ?? f.job_experience_level}</span>}
                                {f.job_location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{f.job_location}</span>}
                                {f.job_salary_range && <span>· {SALARY_LABELS[f.job_salary_range as keyof typeof SALARY_LABELS] ?? f.job_salary_range}</span>}
                              </div>
                            </div>
                          </div>
                        )}
                        {f.overall_comment && <p className="text-sm text-gray-600 line-clamp-2 mb-3">{f.overall_comment}</p>}
                        <button
                          onClick={e => { e.stopPropagation(); setViewSubmitted(f) }}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition mt-auto"
                        >
                          <Eye className="w-4 h-4" /> View Details
                        </button>
                      </div>
                    )
                  })}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────── */}
      {viewRequest && (
        <ViewRequestDetailModal
          request={viewRequest}
          onClose={() => setViewRequest(null)}
          onAccept={() => handleRespond(viewRequest.id, 'accepted')}
          onDecline={() => handleRespond(viewRequest.id, 'declined')}
          onSubmit={() => setSubmitTarget(viewRequest)}
          responding={respondingId === viewRequest.id}
        />
      )}
      {submitTarget && (
        <SubmitFeedbackModal
          request={submitTarget}
          onClose={() => setSubmitTarget(null)}
          onSubmitted={() => { setSubmitTarget(null); fetchAll() }}
        />
      )}
      {viewSubmitted && (
        <ViewSubmittedModal
          feedback={viewSubmitted}
          onClose={() => setViewSubmitted(null)}
        />
      )}
      {companyModalRequest && (
        <CompanyInfoModal
          request={companyModalRequest}
          onClose={() => setCompanyModalRequest(null)}
        />
      )}
    </div>
  )
}
